import React, { useEffect, useState, useMemo } from "react";
import { Modal, Button, message, Select, Input, Form, Row, Col } from "antd";
import {
    BuyerBrief,
    EvaluationGoal,
    SupplierType,
    SUPPLIER_TYPE_LABELS,
    EVALUATION_GOAL_LABELS,
    SUPPLIER_TYPE_GROUPS,
    SUPPLIER_TYPE_GROUP_LABELS,
    SupplierTypeGroup,
} from "../../types/BuyerBriefTypes";
import { createBrief } from "../../common/api/BuyerBriefClient";
import { loadVendorSuggestions } from "../../common/api/VendorClient";
import { Vendor } from "../../types/VendorTypes";
import { AutoComplete } from "../../design-system";
import { BriefIntakeSeed } from "./briefIntakeParams";
import { SvgIcon } from "../../common/components/SvgIcon";
import { CompanyImage } from "../../common/components/CompanyImage";
import {
    TYPE_TOPICS,
    DEEP_DIVE_TOPICS,
    GOAL_RECOMMENDATIONS,
    DEEP_DIVE_IDS,
    TopicOption,
    getTopicsForType,
    FINANCIAL_TOPICS,
    MARKET_TOPICS,
    PRICING_TOPICS,
} from "./topicConfig";
import { TopicCard } from "./components/TopicCard";
import { BriefReviewStep } from "./components/BriefReviewStep";
import styles from "./BuyerBriefWizard.module.less";

interface Props {
    open: boolean;
    onClose: () => void;
    /** Receives the created brief so the caller can land on its tab. */
    onComplete: (created?: BuyerBrief) => void;
    /** Pre-fills supplier details, e.g. when opened from a deal analysis project. */
    initialData?: BriefIntakeSeed;
}

const STEP_LABELS = ["Supplier basics", "Evaluation goal", "Research focus", "Review & confirm"];

const renderVendorOption = (vendor: Vendor) => (
    <>
        <CompanyImage logo={vendor.hero} size="xs" name={vendor.name} />
        <span style={{ paddingLeft: 10 }}>{vendor.name}</span>
    </>
);

const emptyFormData = {
    supplierName: "",
    supplierWebsite: "",
    // Set only when a vendor is picked from the typeahead. This is what lets a
    // deal find its brief later, so free-typed names must never carry one.
    vendorKey: undefined as string | undefined,
    supplierType: undefined as SupplierType | undefined,
    evaluationGoal: undefined as EvaluationGoal | undefined,
    selectedTopics: [] as string[],
};

const seededFormData = (seed?: BriefIntakeSeed) =>
    seed ? { ...emptyFormData, ...seed } : emptyFormData;

const BuyerBriefWizard: React.FC<Props> = ({ open, onClose, onComplete, initialData }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState(() => seededFormData(initialData));

    // The component copies initialData into state, so re-sync when it changes.
    useEffect(() => {
        setFormData(seededFormData(initialData));
        setCurrentStep(0);
    }, [initialData]);
    const [vendorSearch, setVendorSearch] = useState("");
    const [vendorOptions, setVendorOptions] = useState<Vendor[]>([]);

    // Matches VendorInput: no debounce, the AbortController cancels the in-flight
    // request on the next keystroke.
    useEffect(() => {
        const ac = new AbortController();
        loadVendorSuggestions(vendorSearch, { signal: ac.signal })
            .then(setVendorOptions)
            .catch(() => {});
        return () => ac.abort();
    }, [vendorSearch]);

    const handleVendorSelect = (_value: string, option: any) => {
        const vendor: Vendor | undefined = option?.vendor;
        if (!vendor) return;
        setFormData((prev) => ({
            ...prev,
            supplierName: vendor.name,
            vendorKey: vendor.vendorKey,
            supplierWebsite: prev.supplierWebsite || vendor.url || "",
        }));
    };

    // Typing after a selection means the name no longer necessarily refers to
    // the picked vendor, so drop the key rather than let it go stale.
    const handleSupplierNameChange = (value: string) => {
        setFormData((prev) => ({ ...prev, supplierName: value, vendorKey: undefined }));
    };

    // Get visible topics based on spend category
    const visibleTopics = useMemo(() => {
        return getTopicsForType(formData.supplierType);
    }, [formData.supplierType]);

    // Get current goal recommendation
    const goalRecommendation = formData.evaluationGoal
        ? GOAL_RECOMMENDATIONS[formData.evaluationGoal]
        : null;
    const evaluationLabel = formData.evaluationGoal
        ? EVALUATION_GOAL_LABELS[formData.evaluationGoal]
        : "";

    // Reconcile selected topics when goal or spend category changes: prune topics
    // no longer visible for the type (deep dives always survive), then re-apply
    // the goal's on/off recommendation.
    useEffect(() => {
        const visibleIds = new Set(visibleTopics.map((t) => t.id));
        const recommendation = formData.evaluationGoal
            ? GOAL_RECOMMENDATIONS[formData.evaluationGoal]
            : null;
        setFormData((prev) => {
            const topics = new Set(
                prev.selectedTopics.filter(
                    (id) => visibleIds.has(id) || DEEP_DIVE_IDS.includes(id)
                )
            );
            if (recommendation) {
                recommendation.on.forEach((id) => {
                    if (visibleIds.has(id) && !DEEP_DIVE_IDS.includes(id)) {
                        topics.add(id);
                    }
                });
                recommendation.off.forEach((id) => {
                    if (visibleIds.has(id)) {
                        topics.delete(id);
                    }
                });
            }
            return { ...prev, selectedTopics: Array.from(topics) };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.evaluationGoal, formData.supplierType]);

    const updateField = <K extends keyof typeof formData>(
        field: K,
        value: (typeof formData)[K]
    ) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const toggleTopic = (topicId: string) => {
        setFormData((prev) => {
            const topics = new Set(prev.selectedTopics);
            if (topics.has(topicId)) {
                topics.delete(topicId);
            } else {
                topics.add(topicId);
            }
            return { ...prev, selectedTopics: Array.from(topics) };
        });
    };

    const isTopicSelected = (topicId: string) => formData.selectedTopics.includes(topicId);

    const hasTopics = formData.selectedTopics.length > 0;

    const isFormValid = () => {
        return (
            formData.supplierName.trim() &&
            formData.supplierType &&
            formData.evaluationGoal &&
            hasTopics
        );
    };

    const stepValid: boolean[] = [
        Boolean(formData.supplierName.trim() && formData.supplierType),
        Boolean(formData.evaluationGoal),
        hasTopics,
        Boolean(isFormValid()),
    ];

    const goNext = () => setCurrentStep((s) => Math.min(s + 1, STEP_LABELS.length - 1));
    const goBack = () => setCurrentStep((s) => Math.max(s - 1, 0));

    const validate = (): boolean => {
        if (!formData.supplierName.trim() || !formData.supplierType || !formData.evaluationGoal) {
            message.error("Please complete all required fields.");
            return false;
        }
        if (!hasTopics) {
            message.error("Please select at least one research topic.");
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!validate()) {
            return;
        }

        const typeGroup = Object.entries(SUPPLIER_TYPE_GROUPS).find(([_, types]) =>
            types.includes(formData.supplierType!)
        )?.[0] as SupplierTypeGroup;
        if (!typeGroup) {
            message.error("Invalid supplier type. Please try again.");
            return;
        }

        setIsSubmitting(true);
        try {
            const created = await createBrief({
                supplierName: formData.supplierName,
                supplierWebsite: formData.supplierWebsite || undefined,
                vendorKey: formData.vendorKey,
                supplierType: formData.supplierType!,
                supplierTypeGroup: typeGroup,
                evaluationGoal: formData.evaluationGoal!,
                selectedTopics: formData.selectedTopics,
            });
            message.success("Brief submitted — research is running in the background.");
            handleReset();
            onComplete(created);
        } catch (error) {
            message.error("Failed to create brief. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleReset = () => {
        setCurrentStep(0);
        setFormData(seededFormData(initialData));
    };

    const handleClose = () => {
        handleReset();
        onClose();
    };

    // Group topics by category for rendering
    const renderTopicSection = (title: string, topics: TopicOption[]) => {
        if (topics.length === 0) return null;
        return (
            <>
                <div className={styles.topicCategory}>{title}</div>
                {topics.map((topic) => (
                    <TopicCard
                        key={topic.id}
                        {...topic}
                        checked={isTopicSelected(topic.id)}
                        onChange={toggleTopic}
                    />
                ))}
            </>
        );
    };

    const renderBasicsStep = () => (
        <>
            <div className={styles.sectionTitle}>Supplier Identification</div>
            <div className={styles.sectionSub}>Who are you evaluating?</div>
            <Form layout="vertical">
                <Row gutter={16}>
                    {/* No `name` prop on these Form.Items: antd v4 lets the form store
                        override a controlled child's `value`, which would leave a
                        pre-filled formData rendering blank. formData is the single
                        source of truth; validation is handled by stepValid/validate. */}
                    <Col span={12}>
                        <Form.Item label="Supplier Name" required>
                            <AutoComplete
                                placeholder="e.g. Acme Corporation"
                                value={formData.supplierName}
                                options={vendorOptions.map((vendor) => ({
                                    value: vendor.name,
                                    label: renderVendorOption(vendor),
                                    vendor,
                                }))}
                                onSearch={setVendorSearch}
                                onChange={handleSupplierNameChange}
                                onSelect={handleVendorSelect}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="Supplier Website">
                            <Input
                                placeholder="e.g. https://www.acme.com"
                                value={formData.supplierWebsite}
                                onChange={(e) => updateField("supplierWebsite", e.target.value)}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="spendCategory"
                            label="Spend Category"
                            rules={[{ required: true, message: "Please select a category" }]}
                        >
                            <Select
                                placeholder="Select spend category"
                                value={formData.supplierType}
                                onChange={(value) => updateField("supplierType", value)}
                                options={Object.entries(SUPPLIER_TYPE_GROUPS).map(
                                    ([groupkey, categories]) => ({
                                        label: SUPPLIER_TYPE_GROUP_LABELS[
                                            groupkey as SupplierTypeGroup
                                        ],
                                        options: categories.map((c) => ({
                                            value: c,
                                            label: SUPPLIER_TYPE_LABELS[c],
                                        })),
                                    })
                                )}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </>
    );

    const renderGoalStep = () => (
        <>
            <div className={styles.sectionTitle}>Evaluation Goal</div>
            <div className={styles.sectionSub}>
                What decision is this brief supporting? We'll recommend research topics to match.
            </div>
            <Form layout="vertical">
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="evaluationGoal"
                            label="Evaluation Goal"
                            rules={[
                                { required: true, message: "Please select an evaluation goal." },
                            ]}
                        >
                            <Select
                                placeholder="Select evaluation goal"
                                value={formData.evaluationGoal}
                                onChange={(value) => updateField("evaluationGoal", value)}
                                options={Object.entries(EVALUATION_GOAL_LABELS).map(
                                    ([key, label]) => ({ value: key, label })
                                )}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
            {goalRecommendation && (
                <div className={styles.goalBanner}>
                    Recommended for <strong>{evaluationLabel}</strong>:{" "}
                    {goalRecommendation.banner}
                </div>
            )}
        </>
    );

    const renderTopicsStep = () => (
        <div>
            <div className={styles.sectionTitle}>Research Focus</div>
            <div className={styles.sectionSub}>
                We've pre-selected topics for your evaluation goal. Customize as needed.
            </div>

            {goalRecommendation && (
                <div className={styles.goalBanner}>
                    Recommended for <strong>{evaluationLabel}</strong>:{" "}
                    {goalRecommendation.banner}
                </div>
            )}

            <div className={styles.topicGrid}>
                {/* Core Topics */}
                {renderTopicSection("Financial & Risk Intelligence", FINANCIAL_TOPICS)}
                {renderTopicSection("Market & Competitive Position", MARKET_TOPICS)}
                {renderTopicSection("Pricing & Commercial Terms", PRICING_TOPICS)}

                {/* Category-specific Topics */}
                {formData.supplierType &&
                    renderTopicSection(
                        "Category-Specific",
                        TYPE_TOPICS.filter((t) => t.forTypes?.includes(formData.supplierType!))
                    )}

                {/* Deep Dive Topics */}
                {renderTopicSection("Optional Deep Dives", DEEP_DIVE_TOPICS)}
            </div>
        </div>
    );

    const renderReviewStep = () => (
        <BriefReviewStep
            supplierName={formData.supplierName}
            supplierWebsite={formData.supplierWebsite}
            vendorKey={formData.vendorKey}
            supplierType={formData.supplierType}
            evaluationGoal={formData.evaluationGoal}
            selectedTopics={formData.selectedTopics}
            onEditStep={setCurrentStep}
        />
    );

    const stepContent = [renderBasicsStep, renderGoalStep, renderTopicsStep, renderReviewStep];

    return (
        <Modal
            className={styles.wizardModal}
            open={open}
            onCancel={handleClose}
            title="New Buyer Brief"
            width={720}
            closeIcon={<SvgIcon name="close" size={14} />}
            footer={null}
            maskClosable={false}
            maskStyle={{
                background: "rgba(20, 30, 46, 0.95)",
                backdropFilter: "blur(25px)",
            }}
            destroyOnClose
        >
            <div className={styles.stepsBar}>
                {STEP_LABELS.map((label, i) => (
                    <span
                        key={label}
                        className={
                            i < currentStep
                                ? styles.stepComplete
                                : i === currentStep
                                ? styles.stepCurrent
                                : styles.step
                        }
                    >
                        {i + 1}. {label}
                    </span>
                ))}
            </div>

            <div className={styles.stepContent}>{stepContent[currentStep]()}</div>

            <div className={styles.footer}>
                {currentStep === 0 ? (
                    <Button type="link" onClick={handleClose}>
                        Cancel
                    </Button>
                ) : (
                    <Button type="link" onClick={goBack} disabled={isSubmitting}>
                        Back
                    </Button>
                )}
                {currentStep < STEP_LABELS.length - 1 ? (
                    <Button type="primary" disabled={!stepValid[currentStep]} onClick={goNext}>
                        Next
                    </Button>
                ) : (
                    <Button
                        type="primary"
                        loading={isSubmitting}
                        disabled={!stepValid[currentStep]}
                        onClick={handleSubmit}
                    >
                        Generate Brief
                    </Button>
                )}
            </div>
        </Modal>
    );
};

export default BuyerBriefWizard;
